import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import ModeCard from '../../components/docs/ModeCard';
import CodeBlock from '../../components/docs/CodeBlock';
import Callout from '../../components/docs/Callout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'supported-models', label: 'Supported Models' },
  { id: 'api-integration', label: 'API Integration' },
  { id: 'prompt-structure', label: 'Prompt Structure' },
  { id: 'response-parsing', label: 'Response Parsing' },
];

const systemPromptExample = `You are an autonomous day trading agent managing a simulated portfolio.
Your task is to analyze market data and make trading decisions.

RULES:
- Always set stop_loss and take_profit levels
- Respond ONLY with valid JSON
- Base decisions on price action analysis

Current state:
- Position: {{position}}
- Balance: {{balance}}
- Instrument: {{instrument}}`;

const responseSchemaExample = `{
  "action": "LONG",
  "instrument": "NAS",
  "confidence": 0.75,
  "reasoning": "Price broke above 20 EMA with increasing volume. RSI at 58 shows momentum without being overbought. MACD histogram turning positive.",
  "stop_loss": 21480.0,
  "take_profit": 21650.0
}`;

const visionPromptExample = `[Image: 15-minute candlestick chart]

Analyze this chart and decide your trading action.
The chart shows price action across multiple timeframes.

Current position: NONE
Account balance: $102,450`;

const LLMIntegration = () => {
  const seoConfig = SEO_CONFIG['/docs/llm-integration'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="supported-models">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/llm-integration"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/llm-integration" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        LLM Integration
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        How we connect to various LLM providers, structure prompts, and parse trading decisions.
        Each model receives identical market information for fair comparison.
      </p>

      {/* Section 1: Supported Models */}
      <section id="supported-models" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          Supported Models
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          The benchmark includes models from major AI providers. Each model is tested in either
          text mode (structured data) or vision mode (chart images).
        </p>

        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Model</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Provider</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Mode</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">Claude Opus 4.6</td>
                <td className="py-3 px-4 text-zinc-400">Anthropic</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">Claude Opus 4.5</td>
                <td className="py-3 px-4 text-zinc-400">Anthropic</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">Claude Haiku 4.5</td>
                <td className="py-3 px-4 text-zinc-400">Anthropic</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">Text</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">Gemini 3 Pro</td>
                <td className="py-3 px-4 text-zinc-400">Google</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">Gemini 3 Flash</td>
                <td className="py-3 px-4 text-zinc-400">Google</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">Text</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">GPT-5.2</td>
                <td className="py-3 px-4 text-zinc-400">OpenAI</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">GPT-5 Mini</td>
                <td className="py-3 px-4 text-zinc-400">OpenAI</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">Grok 4</td>
                <td className="py-3 px-4 text-zinc-400">xAI</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">Grok 4.1</td>
                <td className="py-3 px-4 text-zinc-400">xAI</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">GLM-4.6V</td>
                <td className="py-3 px-4 text-zinc-400">Z.ai</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">GLM-4.7</td>
                <td className="py-3 px-4 text-zinc-400">Z.ai</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">Text</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-200 font-medium">DeepSeek V3.2</td>
                <td className="py-3 px-4 text-zinc-400">DeepSeek</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">Text</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-zinc-200 font-medium">Kimi K2.5</td>
                <td className="py-3 px-4 text-zinc-400">Moonshot</td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-violet-500/10 text-violet-400 text-xs rounded">Vision</span></td>
                <td className="py-3 px-4"><span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">Active</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ModeCard
            mode="TEXT"
            color="green"
            description="Models receive structured numerical data including OHLCV candles and market context as formatted text."
            models={['Claude Haiku 4.5', 'Gemini 3 Flash', 'DeepSeek V3.2', 'GLM-4.7']}
          />
          <ModeCard
            mode="VISION"
            color="purple"
            description="Models receive rendered candlestick chart images. They analyze visual price patterns to make trading decisions."
            models={['Claude Opus 4.6', 'Claude Opus 4.5', 'Gemini 3 Pro', 'GPT-5.2', 'GPT-5 Mini', 'Grok 4', 'Grok 4.1', 'GLM-4.6V', 'Kimi K2.5']}
          />
        </div>
      </section>

      {/* Section 2: API Integration */}
      <section id="api-integration" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          API Integration
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Each provider has a dedicated API client that handles authentication, rate limiting,
          and response parsing.
        </p>

        <div className="space-y-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                <span className="text-orange-500 font-bold text-sm">A</span>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-200 mb-1">Anthropic (Claude)</h4>
                <p className="text-sm text-zinc-500 mb-2">
                  Uses the Messages API with support for vision inputs. JSON mode ensures structured responses.
                </p>
                <code className="text-xs text-zinc-600 bg-dark-surface px-2 py-1 rounded break-all">api.anthropic.com/v1/messages</code>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                <span className="text-blue-500 font-bold text-sm">G</span>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-200 mb-1">Google (Gemini)</h4>
                <p className="text-sm text-zinc-500 mb-2">
                  Uses the Generative AI SDK with multimodal support for chart images.
                </p>
                <code className="text-xs text-zinc-600 bg-dark-surface px-2 py-1 rounded break-all">generativelanguage.googleapis.com</code>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                <span className="text-emerald-500 font-bold text-sm">O</span>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-200 mb-1">OpenAI (GPT)</h4>
                <p className="text-sm text-zinc-500 mb-2">
                  Chat Completions API with vision support for chart analysis.
                </p>
                <code className="text-xs text-zinc-600 bg-dark-surface px-2 py-1 rounded break-all">api.openai.com/v1/chat/completions</code>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-zinc-500/10 flex items-center justify-center shrink-0">
                <span className="text-zinc-400 font-bold text-sm">X</span>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-200 mb-1">xAI (Grok)</h4>
                <p className="text-sm text-zinc-500 mb-2">
                  OpenAI-compatible API with vision capabilities for chart analysis.
                </p>
                <code className="text-xs text-zinc-600 bg-dark-surface px-2 py-1 rounded break-all">api.x.ai/v1/chat/completions</code>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                <span className="text-green-500 font-bold text-sm">D</span>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-200 mb-1">DeepSeek</h4>
                <p className="text-sm text-zinc-500 mb-2">
                  OpenAI-compatible API optimized for reasoning tasks.
                </p>
                <code className="text-xs text-zinc-600 bg-dark-surface px-2 py-1 rounded break-all">api.deepseek.com/v1/chat/completions</code>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                <span className="text-cyan-500 font-bold text-sm">Z</span>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-200 mb-1">Z.ai (GLM)</h4>
                <p className="text-sm text-zinc-500 mb-2">
                  OpenAI-compatible API with multimodal capabilities.
                </p>
                <code className="text-xs text-zinc-600 bg-dark-surface px-2 py-1 rounded break-all">open.bigmodel.cn/api/paas/v4/chat/completions</code>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                <span className="text-purple-500 font-bold text-sm">M</span>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-200 mb-1">Moonshot (Kimi)</h4>
                <p className="text-sm text-zinc-500 mb-2">
                  Natively multimodal API with thinking mode for deep reasoning.
                </p>
                <code className="text-xs text-zinc-600 bg-dark-surface px-2 py-1 rounded break-all">api.moonshot.cn/v1/chat/completions</code>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Section 3: Prompt Structure */}
      <section id="prompt-structure" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Prompt Structure
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Prompts are carefully structured to provide all necessary context while maintaining
          consistency across different models.
        </p>

        <Callout type="info" title="Simplified Examples">
          The prompts shown below are simplified examples for illustration purposes.
          The actual prompts include additional market context and formatting.
        </Callout>

        <h3 className="font-semibold text-zinc-200 mb-3 mt-6">System Prompt Template</h3>
        <CodeBlock filename="system_prompt.txt" code={systemPromptExample} />

        <h3 className="font-semibold text-zinc-200 mb-3 mt-6">Vision Mode Prompt</h3>
        <CodeBlock filename="vision_prompt.txt" code={visionPromptExample} />
      </section>

      {/* Section 4: Response Parsing */}
      <section id="response-parsing" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">04</span>
          Response Parsing
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Models must respond with valid JSON matching the expected schema. Invalid responses
          are logged and treated as HOLD decisions.
        </p>

        <h3 className="font-semibold text-zinc-200 mb-3">Expected Response Format</h3>
        <CodeBlock filename="response.json" code={responseSchemaExample} />

        <div className="mt-6 space-y-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-2">Valid Actions</h4>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-green-500/10 text-green-500 text-sm rounded font-mono">LONG</span>
              <span className="px-3 py-1 bg-red-500/10 text-red-500 text-sm rounded font-mono">SHORT</span>
              <span className="px-3 py-1 bg-zinc-500/10 text-zinc-400 text-sm rounded font-mono">HOLD</span>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5 mb-6">
            <h4 className="font-semibold text-zinc-200 mb-2">Validation Rules</h4>
            <ul className="space-y-2 text-sm text-zinc-500">
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Action must be one of: LONG, SHORT, HOLD</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Confidence must be between 0.0 and 1.0</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Stop loss must be set for LONG/SHORT actions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Take profit must be set for LONG/SHORT actions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Reasoning should explain the decision</span>
              </li>
            </ul>
          </div>
        </div>

        <Callout type="warning" title="Parse Failures">
          If a model returns invalid JSON or missing fields, the decision defaults to HOLD.
          Parse failures are tracked and displayed in the model's statistics.
        </Callout>
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs/leaderboard"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">Leaderboard System</div>
          </div>
        </Link>
        <Link
          to="/docs/decision-process"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">Decision Process</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default LLMIntegration;
